import { ArrayUtils } from "../utilities/arrays";
import * as moment from "moment";

export const DateExpressions: DateExpression[] = [{
    key: "Today",
    expression: () => new Date(),
    unit: 'd',
    unitText: "day"
},
{
    key: "Now",
    expression: () => new Date(),
    unit: 'm'
},
{
    key: "Yesterday",
    expression: () => moment().subtract(1, "days").toDate(),
    unit: 'd',
    unitText: "day"
},
{
    key: "Week",
    expression: () => moment().startOf('isoWeek').toDate(),
    unit: 'w',
    unitText: "isoWeek"
},
{
    key: "Month",
    expression: () => moment().startOf('month').toDate(),
    unit: 'M',
    unitText: "month"
},
{
    key: "Quarter",
    expression: () => moment().startOf('quarter').toDate(),
    unit: 'Q',
    unitText: "quarter"
},
{
    key: "Year",
    expression: () => moment().startOf('year').toDate(),
    unit: 'y',
    unitText: "year"
},
{
    key: "MMMM YYYY",
    expression: (data) => moment(data, "MMMM YYYY").startOf('month').toDate(),
    unit: 'M',
    unitText: "month",
    isFormat: true
},
{
    key: "MMM YYYY",
    expression: (data) => moment(data, "MMM YYYY").startOf('month').toDate(),
    unit: 'M',
    unitText: "month",
    isFormat: true
},
{
    key: "MM YYYY",
    expression: (data) => moment(data, "MM YYYY").startOf('month').toDate(),
    unit: 'M',
    unitText: "month",
    isFormat: true
},
{
    key: "YYYY",
    expression: (data) => moment(data, "YYYY").startOf('year').toDate(),
    unit: 'y',
    unitText: "year",
    isFormat: true
}
];


export interface DateExpression {
    key: string;
    expression: (data: any) => Date;
    unit: string;
    unitText?: string;
    // indicates that the date expression key is a date format
    isFormat?: boolean;
}

export class DateExpressionParser {

    public static isValidExpression(expression: string | Date) {
        return DateExpressionParser.parseDateInterceptor(expression, (parsed: boolean) => parsed);
    }

    //matchByKey is used to see if the expression should be exactly the same as the key, used for format expressions
    public static getDateValue(expression: string | Date, data: any, matchByKey: boolean = false) {
        return DateExpressionParser.parseDateInterceptor(expression, (parsed: boolean, index: number, isPlus: boolean, constantNumber: number) => {
            if (parsed && !constantNumber) {
                return DateExpressions[index].expression(data);
            } else if (parsed && constantNumber) {
                let date = DateExpressions[index].expression(data);
                if (isPlus) {
                    date = new Date(moment(date).add(<any>Number(constantNumber), DateExpressions[index].unit).valueOf());
                } else {
                    date = new Date(moment(date).subtract(<any>Number(constantNumber), DateExpressions[index].unit).valueOf());
                }
                return date;
            } else {
                return undefined;
            }
        }, matchByKey);
    }

    private static parseDateInterceptor(expression: string | Date, onReturn: (parsed: boolean, index: number, isPlus: boolean, constantNumber: number) => any, matchByKey: boolean = false) {
        if (!expression) {
            return false;
        }
        let expStr = matchByKey ? expression.toString() : expression.toString().replace(/ /g, '').toLowerCase();
        const index = ArrayUtils.findIndex(matchByKey ? DateExpressions : DateExpressions.filter(x => x.isFormat != true), x => matchByKey ? expStr == x.key : expStr.indexOf(x.key.toLowerCase()) > -1);
        if (index > -1) {
            const key = DateExpressions[index].key.trim().toLowerCase();
            const indexKey = expStr.indexOf(key);
            expStr = expStr.substr(indexKey + key.length);
            if (expStr.length > 0 && (expStr[0] == '+' || expStr[0] == '-')) {
                const constantNumber = expStr.substr(1);
                if (DateExpressionParser.isInt(constantNumber)) {
                    return onReturn(true, index, expStr[0] == '+', Number(constantNumber));
                } else {
                    return onReturn(false, -1, false, null);
                }
            } else {
                return onReturn(true, index, expStr[0] == '+', 0);
            }
        }
        return onReturn(false, -1, false, null);
    }

    public static isInt(value: any) {
        return !isNaN(value) &&
            parseInt(Number(value).toString()) == value &&
            !isNaN(parseInt(value, 10));
    }

    public static isDateExpression(expression: any) {
        if (!expression) {
            return false;
        }
        const expStr = expression.toString().replace(/ /g, '').toLowerCase();
        const index = ArrayUtils.findIndex(DateExpressions, x => expStr.indexOf(x.key.toLowerCase()) > -1);
        if (index > -1) {
            return true;
        }

        return false;
    }

    public static isExpressionDateRange(expression: any, matchByKey: boolean = false) {
        if (!expression) {
            return false;
        }
        const expStr = matchByKey ? expression.toString() : expression.toString().replace(/ /g, '').toLowerCase();
        const index = ArrayUtils.findIndex(DateExpressions, x => matchByKey ? expStr == x.key : expStr.indexOf(x.key.toLowerCase()) > -1);
        if (index > -1) {
            return DateExpressions[index].unit == 'd' ||
                DateExpressions[index].unit == 'w' ||
                DateExpressions[index].unit == 'M' ||
                DateExpressions[index].unit == 'Q' ||
                DateExpressions[index].unit == 'y';
        }

        return false;
    }

    public static getEndDateFromRangeExpression(expression: any, date: Date, matchByKey: boolean = false) {
        if (!expression || !DateExpressionParser.isExpressionDateRange(expression)) {
            return null;
        }
        const expStr = matchByKey ? expression.toString() : expression.toString().replace(/ /g, '').toLowerCase();
        const index = ArrayUtils.findIndex(DateExpressions, x => matchByKey ? expStr == x.key : expStr.indexOf(x.key.toLowerCase()) > -1);
        if (index > -1) {
            if (DateExpressions[index].unitText && DateExpressions[index].unitText != null) {
                return moment(date).endOf(<any>DateExpressions[index].unitText);
            }
        }

        return null;
    }
}
